import React from 'react';
import { useParams } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { campaignService } from '../services/campaignService';
import { formatCompactNumber, formatPercent } from '../utils/format';
import Skeleton from '../components/ui/Skeleton';
import EventBus from '../utils/EventBus';
import styles from './ChannelPerformance.module.css';

const CHANNEL_NAMES = {
  push: 'Push + In-app',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  email: 'Email',
  rcs: 'RCS',
};

const FUNNEL_STEPS = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  { key: 'converted', label: 'Converted' },
];
const FUNNEL_COLORS = ['#686868', '#4878B0', '#C09848', '#8878C0', '#4F9B6F'];

const DAYS = 30;

class ChannelPerformance extends React.Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, stats: null };
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.channel !== this.props.channel) this.load();
  }

  load() {
    const { channel } = this.props;
    this.setState({ loading: true });
    campaignService
      .channelStats(channel, DAYS)
      .then((stats) => this.setState({ loading: false, stats }))
      .catch((err) => {
        this.setState({ loading: false, stats: null });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load channel performance.' });
      });
  }

  render() {
    const { channel } = this.props;
    const { loading, stats } = this.state;
    const name = CHANNEL_NAMES[channel] || channel;

    if (loading || !stats) {
      return (
        <div className={styles.loadingWrap}>
          <Skeleton variant="text" width={240} height={20} />
          <Skeleton variant="rect" width="100%" height={200} style={{ marginTop: 16, borderRadius: 12 }} />
        </div>
      );
    }

    const funnel = stats.funnel || {};
    const maxFunnel = Math.max(1, funnel.sent || 0);
    const daily = (stats.daily || []).map((r) => ({
      day: new Date(r.day).toLocaleDateString([], { month: 'short', day: 'numeric' }),
      sent: Number(r.sent),
      delivered: Number(r.delivered),
      clicked: Number(r.clicked),
    }));

    return (
      <>
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>{name}</div>
            <div className={styles.pageSub}>
              {stats.has_data ? `Delivery · engagement · usage · last ${DAYS} days` : 'No campaigns sent on this channel yet'}
            </div>
          </div>
        </div>

        <div className={styles.statRow}>
          <div className={styles.statCard}>
            <div className={styles.statVal}>{formatCompactNumber(stats.sample_size)}</div>
            <div className={styles.statLbl}>Sent</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statVal}>{formatPercent((stats.delivery_rate || 0) * 100)}</div>
            <div className={styles.statLbl}>Delivery rate</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statVal}>{formatPercent((stats.click_rate || 0) * 100)}</div>
            <div className={styles.statLbl}>Click rate</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statVal}>{formatPercent((stats.conversion_rate || 0) * 100)}</div>
            <div className={styles.statLbl}>Conversion rate</div>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Funnel</div>
          </div>
          <div className={styles.funnelWrap}>
            {FUNNEL_STEPS.map((s, i) => {
              const count = funnel[s.key] || 0;
              const pct = maxFunnel ? Math.round((count / maxFunnel) * 100) : 0;
              const col = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
              return (
                <div key={s.key} className={styles.funnelRow}>
                  <div className={styles.funnelBar} style={{ width: pct + '%', background: col + '22', borderLeft: '3px solid ' + col }} />
                  <div className={styles.funnelLabel}>{s.label}</div>
                  <div className={styles.funnelCount} style={{ color: col }}>{formatCompactNumber(count)}</div>
                  <div className={styles.funnelPct}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Usage</div>
            <div className={styles.cardSub}>Sent vs delivered vs clicked · last {DAYS} days</div>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={daily} margin={{ top: 8, right: 16, left: -20, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--b1)" vertical={false} />
              <XAxis dataKey="day" tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--t3)', fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: 'var(--s3)', border: '1px solid var(--b2)', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11, color: 'var(--t3)' }} />
              <Area type="monotone" dataKey="sent" name="Sent" stroke="#686868" fill="#68686822" />
              <Area type="monotone" dataKey="delivered" name="Delivered" stroke="#4878B0" fill="#4878B022" />
              <Area type="monotone" dataKey="clicked" name="Clicked" stroke="#8878C0" fill="#8878C022" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </>
    );
  }
}

function ChannelPerformanceWithRouter() {
  const { channel } = useParams();
  return <ChannelPerformance channel={channel} />;
}

export default ChannelPerformanceWithRouter;
